import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import type {
  DuplicateCheckResult,
  FeatureRequest,
  TranscriptSource,
  UploadSummary,
} from '../types/featureRequests';
import type { FilterOption, UserFilterOption } from '../types';
import { useFeatureRequestStore } from '../stores/featureRequestStore';
import { getFilterOptions } from '../api/dashboard';

const SOURCE_OPTIONS: { value: TranscriptSource; label: string }[] = [
  { value: 'customer_call', label: 'Customer Call' },
  { value: 'demo_call', label: 'Demo Call' },
  { value: 'meeting_notes', label: 'Meeting Notes' },
  { value: 'slack', label: 'Slack' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#94a3b8',
};

function FeatureRequests() {
  const {
    transcript,
    transcriptSource,
    callerOrgId,
    callerUserId,
    callDate,
    isProcessing,
    isLoadingFeatures,
    processingError,
    featureRequests,
    extractionSummary,
    selectedFeatureIds,
    duplicateResults,
    checkingDuplicateId,
    linearTeams,
    selectedTeamId,
    isCreatingTickets,
    creationResults,
    uploads,
    isLoadingHistory,
    setTranscript,
    setTranscriptSource,
    setCallerOrg,
    setCallerUser,
    setCallDate,
    processTranscriptAction,
    loadUploadFeatures,
    loadUploads,
    loadLinearTeams,
    loadUploadTranscript,
    currentTranscript,
    currentUploadId,
    currentUpload,
    updateUploadDetails,
    deleteCurrentUpload,
    toggleFeatureSelection,
    selectAllFeatures,
    clearSelection,
    setSelectedTeam,
    checkDuplicatesAction,
    createSingleTicket,
    createSelectedTickets,
    reset,
    clearError,
  } = useFeatureRequestStore();

  // Filter options for org/user dropdowns
  const [filterOptions, setFilterOptions] = useState<{
    orgIds: FilterOption[];
    userIds: UserFilterOption[];
  }>({ orgIds: [], userIds: [] });
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Duplicate check modal
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateModalData, setDuplicateModalData] = useState<{
    featureId: string;
    result: DuplicateCheckResult | null;
  } | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Transcript modal
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);

  // Edit meeting details modal
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [editOrgId, setEditOrgId] = useState<string | null>(null);
  const [editOrgName, setEditOrgName] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editUserName, setEditUserName] = useState<string | null>(null);
  const [editCallDate, setEditCallDate] = useState<string | null>(null);
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load initial data
  useEffect(() => {
    loadUploads();
    loadLinearTeams();
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    setLoadingFilters(true);
    try {
      const options = await getFilterOptions();
      setFilterOptions({
        orgIds: options.orgIds,
        userIds: options.userIds,
      });
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
    setLoadingFilters(false);
  };

  const handleProcessTranscript = async () => {
    await processTranscriptAction();
  };

  const handleCheckDuplicates = async (featureId: string) => {
    const result = await checkDuplicatesAction(featureId);
    setDuplicateModalData({ featureId, result });
    setDuplicateModalOpen(true);
  };

  const handleCreateTicket = async (featureId: string, skipDuplicateCheck = false) => {
    const success = await createSingleTicket(featureId, skipDuplicateCheck);
    if (success) {
      setSnackbar({
        open: true,
        message: 'Linear ticket created successfully!',
        severity: 'success',
      });
    }
  };

  const handleBulkCreate = async (skipDuplicateCheck = false) => {
    await createSelectedTickets(skipDuplicateCheck);
    if (creationResults) {
      setSnackbar({
        open: true,
        message: `Created ${creationResults.success} tickets${creationResults.failed > 0 ? `, ${creationResults.failed} failed` : ''}`,
        severity: creationResults.failed > 0 ? 'info' : 'success',
      });
    }
  };

  const handleUploadClick = (upload: UploadSummary) => {
    loadUploadFeatures(upload.id);
  };

  const handleViewTranscript = async () => {
    if (!currentUploadId) return;
    setLoadingTranscript(true);
    setTranscriptModalOpen(true);
    await loadUploadTranscript(currentUploadId);
    setLoadingTranscript(false);
  };

  const handleOpenEditDetails = () => {
    if (currentUpload) {
      // Use IDs directly if available, otherwise try to match by name
      let orgId = currentUpload.callerOrgId;
      let userId = currentUpload.callerUserId;

      if (!orgId && currentUpload.callerOrgName) {
        const matchingOrg = filterOptions.orgIds.find(
          (o) => o.name === currentUpload.callerOrgName
        );
        orgId = matchingOrg?.id || null;
      }
      if (!userId && currentUpload.callerUserName) {
        const matchingUser = filterOptions.userIds.find(
          (u) => u.name === currentUpload.callerUserName
        );
        userId = matchingUser?.id || null;
      }

      setEditOrgId(orgId);
      setEditOrgName(currentUpload.callerOrgName);
      setEditUserId(userId);
      setEditUserName(currentUpload.callerUserName);
      setEditCallDate(currentUpload.callDate);
    }
    setEditDetailsOpen(true);
  };

  const handleSaveDetails = async () => {
    setIsSavingDetails(true);
    const success = await updateUploadDetails({
      callerOrgId: editOrgId,
      callerOrgName: editOrgName,
      callerUserId: editUserId,
      callerUserName: editUserName,
      callDate: editCallDate,
    });
    setIsSavingDetails(false);
    if (success) {
      setEditDetailsOpen(false);
      setSnackbar({
        open: true,
        message: 'Meeting details updated',
        severity: 'success',
      });
    }
  };

  const handleDeleteUpload = async () => {
    setIsDeleting(true);
    const success = await deleteCurrentUpload();
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    if (success) {
      setSnackbar({
        open: true,
        message: 'Transcript deleted',
        severity: 'success',
      });
    }
  };

  const editFilteredUsers = editOrgId
    ? filterOptions.userIds.filter((u) => !u.orgId || u.orgId === editOrgId)
    : filterOptions.userIds;

  const filteredUsers = callerOrgId
    ? filterOptions.userIds.filter((u) => !u.orgId || u.orgId === callerOrgId)
    : filterOptions.userIds;

  const pendingFeatures = featureRequests.filter((f) => !f.linearIssueId);
  const createdFeatures = featureRequests.filter((f) => f.linearIssueId);

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Sidebar - History */}
      <Box
        sx={{
          width: 280,
          borderRight: '1px solid rgba(148, 163, 184, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              History
            </Typography>
            <IconButton size="small" onClick={loadUploads} disabled={isLoadingHistory}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoadingHistory ? (
            <Box sx={{ p: 2 }}>
              <LinearProgress />
            </Box>
          ) : uploads.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No previous uploads
              </Typography>
            </Box>
          ) : (
            <List dense>
              {uploads.map((upload) => (
                <ListItemButton
                  key={upload.id}
                  onClick={() => handleUploadClick(upload)}
                  selected={upload.id === useFeatureRequestStore.getState().currentUploadId}
                  sx={{
                    borderBottom: '1px solid rgba(148, 163, 184, 0.05)',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(139, 92, 246, 0.1)',
                    },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, alignSelf: 'flex-start', mt: 0.5 }}>
                    <LightbulbIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Tooltip title={upload.summary || ''} placement="right" arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.3,
                          }}
                        >
                          {upload.summary
                            ? upload.summary.slice(0, 80) + (upload.summary.length > 80 ? '...' : '')
                            : upload.callerOrgName || 'Transcript'}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {upload.callerOrgName && `${upload.callerOrgName} · `}
                          {upload.callDate
                            ? format(new Date(upload.callDate), 'MMM d, yyyy')
                            : format(new Date(upload.createdAt), 'MMM d, yyyy')}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            label={`${upload.featureCount} feat`}
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                          {upload.bugCount > 0 && (
                            <Chip
                              size="small"
                              label={`${upload.bugCount} bug`}
                              color="error"
                              sx={{ height: 18, fontSize: '0.7rem' }}
                            />
                          )}
                          {upload.createdTickets > 0 && (
                            <Chip
                              size="small"
                              label={`${upload.createdTickets} created`}
                              color="success"
                              sx={{ height: 18, fontSize: '0.7rem' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={reset}
          >
            New Upload
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Input Section */}
        {featureRequests.length === 0 && !isProcessing && (
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
            <Typography variant="h5" gutterBottom>
              Extract Feature Requests
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste a customer call transcript or meeting notes to extract feature requests and
              bug reports.
            </Typography>

            {processingError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                {processingError}
              </Alert>
            )}

            <Stack spacing={2}>
              {/* Source, Date, and Caller Info */}
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={transcriptSource}
                    label="Source"
                    onChange={(e) => setTranscriptSource(e.target.value as TranscriptSource)}
                  >
                    {SOURCE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="date"
                  label="Call Date"
                  value={callDate || ''}
                  onChange={(e) => setCallDate(e.target.value || null)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />

                <Autocomplete
                  size="small"
                  sx={{ minWidth: 200 }}
                  freeSolo
                  options={filterOptions.orgIds}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
                  loading={loadingFilters}
                  value={filterOptions.orgIds.find((o) => o.id === callerOrgId) || null}
                  onChange={(_, val) => {
                    if (typeof val === 'string') {
                      setCallerOrg(null, val);
                    } else {
                      setCallerOrg(val?.id || null, val?.name || null);
                    }
                    setCallerUser(null, null); // Reset user when org changes
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Organization" placeholder="Type or select..." />
                  )}
                />

                <Autocomplete
                  size="small"
                  sx={{ minWidth: 200 }}
                  freeSolo
                  options={filteredUsers}
                  getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
                  loading={loadingFilters}
                  value={filteredUsers.find((u) => u.id === callerUserId) || null}
                  onChange={(_, val) => {
                    if (typeof val === 'string') {
                      setCallerUser(null, val);
                    } else {
                      setCallerUser(val?.id || null, val?.name || null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Contact" placeholder="Type or select..." />
                  )}
                />
              </Stack>

              {/* Transcript Input */}
              <TextField
                multiline
                rows={10}
                fullWidth
                placeholder="Paste your transcript or meeting notes here..."
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  },
                }}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pt: 1 }}>
                  {transcript.length} characters
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  disabled={isProcessing || transcript.length < 50}
                  onClick={handleProcessTranscript}
                  startIcon={isProcessing ? <CircularProgress size={20} /> : <SearchIcon />}
                >
                  {isProcessing ? 'Extracting...' : 'Extract Features'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Processing Indicator - for new transcript extraction */}
        {isProcessing && featureRequests.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Analyzing transcript...</Typography>
            <Typography variant="body2" color="text.secondary">
              Claude is extracting feature requests and bug reports
            </Typography>
          </Box>
        )}

        {/* Loading Indicator - for loading existing upload features */}
        {isLoadingFeatures && featureRequests.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Loading features...</Typography>
          </Box>
        )}

        {/* Results Section */}
        {featureRequests.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            {/* Loading overlay when switching between uploads */}
            {isLoadingFeatures && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(15, 23, 42, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={48} />
              </Box>
            )}
            {/* Header with summary */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                bgcolor: 'background.paper',
              }}
            >
              {/* Meeting Details */}
              {currentUpload && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: 'rgba(139, 92, 246, 0.05)',
                    borderColor: 'rgba(139, 92, 246, 0.2)',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={3} alignItems="center">
                      {currentUpload.callerOrgName && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Organization:</strong> {currentUpload.callerOrgName}
                          </Typography>
                        </Stack>
                      )}
                      {currentUpload.callerUserName && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Contact:</strong> {currentUpload.callerUserName}
                          </Typography>
                        </Stack>
                      )}
                      {currentUpload.callDate && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <EventIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Date:</strong>{' '}
                            {format(new Date(currentUpload.callDate), 'MMM d, yyyy')}
                          </Typography>
                        </Stack>
                      )}
                      {!currentUpload.callerOrgName &&
                        !currentUpload.callerUserName &&
                        !currentUpload.callDate && (
                          <Typography variant="body2" color="text.secondary">
                            No meeting details set
                          </Typography>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit details">
                        <IconButton size="small" onClick={handleOpenEditDetails}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete transcript">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmOpen(true)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              )}

              {extractionSummary && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {extractionSummary}
                </Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {pendingFeatures.length} pending, {createdFeatures.length} created
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={handleViewTranscript}
                  >
                    View Transcript
                  </Button>

                  {pendingFeatures.length > 0 && (
                    <>
                      <Button
                        size="small"
                        onClick={selectAllFeatures}
                        disabled={selectedFeatureIds.size === pendingFeatures.length}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={clearSelection}
                        disabled={selectedFeatureIds.size === 0}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  {linearTeams.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Team</InputLabel>
                      <Select
                        value={selectedTeamId || ''}
                        label="Team"
                        onChange={(e) => setSelectedTeam(e.target.value || null)}
                      >
                        {linearTeams.map((team) => (
                          <MenuItem key={team.id} value={team.id}>
                            {team.name} ({team.key})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Button
                    variant="contained"
                    disabled={selectedFeatureIds.size === 0 || isCreatingTickets}
                    onClick={() => handleBulkCreate(false)}
                    startIcon={
                      isCreatingTickets ? <CircularProgress size={20} /> : <LinkIcon />
                    }
                  >
                    Create {selectedFeatureIds.size} in Linear
                  </Button>
                </Stack>
              </Stack>

              {processingError && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
                  {processingError}
                </Alert>
              )}

              {creationResults && (
                <Alert
                  severity={creationResults.failed > 0 ? 'warning' : 'success'}
                  sx={{ mt: 2 }}
                >
                  Created {creationResults.success} tickets
                  {creationResults.failed > 0 && `, ${creationResults.failed} failed`}
                </Alert>
              )}
            </Box>

            {/* Feature Cards */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Stack spacing={2}>
                {featureRequests.map((feature) => (
                  <FeatureCard
                    key={feature.id}
                    feature={feature}
                    selected={selectedFeatureIds.has(feature.id)}
                    onToggleSelect={() => toggleFeatureSelection(feature.id)}
                    onCheckDuplicates={() => handleCheckDuplicates(feature.id)}
                    onCreateTicket={(skip) => handleCreateTicket(feature.id, skip)}
                    isCheckingDuplicates={checkingDuplicateId === feature.id}
                    isCreating={isCreatingTickets}
                    duplicateResult={duplicateResults.get(feature.id)}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      {/* Duplicate Check Modal */}
      <Dialog
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Potential Duplicates</DialogTitle>
        <DialogContent>
          {duplicateModalData?.result ? (
            duplicateModalData.result.potentialDuplicates.length > 0 ? (
              <Stack spacing={2}>
                {duplicateModalData.result.hasHighSimilarity && (
                  <Alert severity="warning">
                    High similarity match found. Review before creating.
                  </Alert>
                )}
                {duplicateModalData.result.potentialDuplicates.map((dup) => (
                  <Card key={dup.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="start">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {dup.identifier}: {dup.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {dup.description?.slice(0, 200)}
                            {dup.description && dup.description.length > 200 ? '...' : ''}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip size="small" label={dup.state} />
                            <Chip
                              size="small"
                              label={`${Math.round(dup.similarityScore * 100)}% similar`}
                              color={dup.similarityScore > 0.7 ? 'warning' : 'default'}
                            />
                          </Stack>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => window.open(dup.url, '_blank')}
                        >
                          <LinkIcon />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="success">No similar issues found in Linear.</Alert>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateModalOpen(false)}>Close</Button>
          {duplicateModalData?.featureId && (
            <Button
              variant="contained"
              onClick={() => {
                handleCreateTicket(duplicateModalData.featureId, true);
                setDuplicateModalOpen(false);
              }}
            >
              {duplicateModalData.result?.potentialDuplicates.length
                ? 'Create Anyway'
                : 'Create Ticket'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Transcript Modal */}
      <Dialog
        open={transcriptModalOpen}
        onClose={() => setTranscriptModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ArticleIcon />
            <span>Original Transcript</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loadingTranscript ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : currentTranscript ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: '60vh',
                overflow: 'auto',
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                '& h1, & h2, & h3, & h4': {
                  mt: 2,
                  mb: 1,
                  fontWeight: 600,
                },
                '& p': {
                  my: 1,
                  lineHeight: 1.6,
                },
                '& ul, & ol': {
                  pl: 3,
                  my: 1,
                },
                '& li': {
                  my: 0.5,
                },
                '& blockquote': {
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 1,
                  color: 'text.secondary',
                },
                '& code': {
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': {
                    bgcolor: 'transparent',
                    p: 0,
                  },
                },
                '& hr': {
                  my: 2,
                  border: 'none',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 1,
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: 'rgba(0, 0, 0, 0.03)',
                  fontWeight: 600,
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {currentTranscript}
              </ReactMarkdown>
            </Paper>
          ) : (
            <Alert severity="info">No transcript available.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscriptModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Meeting Details Modal */}
      <Dialog
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Meeting Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              freeSolo
              options={filterOptions.orgIds}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
              loading={loadingFilters}
              value={filterOptions.orgIds.find((o) => o.id === editOrgId) || editOrgName || null}
              onChange={(_, val) => {
                if (typeof val === 'string') {
                  setEditOrgId(null);
                  setEditOrgName(val);
                } else {
                  setEditOrgId(val?.id || null);
                  setEditOrgName(val?.name || null);
                }
                setEditUserId(null);
                setEditUserName(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Organization" placeholder="Type or select..." />
              )}
            />

            <Autocomplete
              size="small"
              freeSolo
              options={editFilteredUsers}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.name}
              loading={loadingFilters}
              value={editFilteredUsers.find((u) => u.id === editUserId) || editUserName || null}
              onChange={(_, val) => {
                if (typeof val === 'string') {
                  setEditUserId(null);
                  setEditUserName(val);
                } else {
                  setEditUserId(val?.id || null);
                  setEditUserName(val?.name || null);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Contact" placeholder="Type or select..." />
              )}
            />

            <TextField
              size="small"
              type="date"
              label="Call Date"
              value={editCallDate || ''}
              onChange={(e) => setEditCallDate(e.target.value || null)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDetailsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDetails}
            disabled={isSavingDetails}
            startIcon={isSavingDetails ? <CircularProgress size={16} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Transcript?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete this transcript and all extracted feature requests.
            Any Linear tickets that were created will remain.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUpload}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Feature Card Component
interface FeatureCardProps {
  feature: FeatureRequest;
  selected: boolean;
  onToggleSelect: () => void;
  onCheckDuplicates: () => void;
  onCreateTicket: (skipDuplicateCheck: boolean) => void;
  isCheckingDuplicates: boolean;
  isCreating: boolean;
  duplicateResult?: DuplicateCheckResult;
}

function FeatureCard({
  feature,
  selected,
  onToggleSelect,
  onCheckDuplicates,
  onCreateTicket,
  isCheckingDuplicates,
  isCreating,
  duplicateResult,
}: FeatureCardProps) {
  const isCreated = !!feature.linearIssueId;
  const isBug = feature.itemType === 'bug';

  return (
    <Card
      sx={{
        opacity: isCreated ? 0.7 : 1,
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'rgba(148, 163, 184, 0.1)',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2}>
          {/* Checkbox */}
          {!isCreated && (
            <Checkbox
              checked={selected}
              onChange={onToggleSelect}
              sx={{
                alignSelf: 'flex-start',
                mt: 0.5,
                ml: -1,
                p: 0.5,
              }}
            />
          )}

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              {isBug ? (
                <BugReportIcon color="error" fontSize="small" />
              ) : (
                <LightbulbIcon color="primary" fontSize="small" />
              )}
              <Typography variant="h6" sx={{ flex: 1 }}>
                {feature.title}
              </Typography>
              <Chip
                size="small"
                label={feature.priority}
                sx={{
                  bgcolor: PRIORITY_COLORS[feature.priority],
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              {feature.confidenceScore && (
                <Tooltip title="AI Confidence">
                  <Chip
                    size="small"
                    label={`${Math.round(feature.confidenceScore * 100)}%`}
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {feature.description}
            </Typography>

            {feature.examples.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Examples:
                </Typography>
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  {feature.examples.map((ex, i) => (
                    <li key={i}>
                      <Typography variant="body2">{ex}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
              {feature.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  sx={{
                    bgcolor:
                      tag === 'PRDRQST'
                        ? 'rgba(139, 92, 246, 0.2)'
                        : tag === 'BUGRQST'
                          ? 'rgba(239, 68, 68, 0.2)'
                          : 'rgba(148, 163, 184, 0.1)',
                  }}
                />
              ))}
              {feature.requestedBy && (
                <Chip size="small" label={`From: ${feature.requestedBy}`} variant="outlined" />
              )}
            </Stack>

            {/* Linear Info or Actions */}
            {isCreated ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Button
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => window.open(feature.linearIssueUrl!, '_blank')}
                >
                  {feature.linearIssueIdentifier}
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onCheckDuplicates}
                  disabled={isCheckingDuplicates}
                  startIcon={
                    isCheckingDuplicates ? (
                      <CircularProgress size={16} />
                    ) : duplicateResult?.hasHighSimilarity ? (
                      <WarningIcon color="warning" />
                    ) : duplicateResult ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                >
                  {duplicateResult
                    ? duplicateResult.hasHighSimilarity
                      ? 'Duplicates Found'
                      : 'No Duplicates'
                    : 'Check Duplicates'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => onCreateTicket(false)}
                  disabled={isCreating}
                  startIcon={<LinkIcon />}
                >
                  Create in Linear
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default FeatureRequests;
